import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SummaryMatchStats } from '../utils/matchEvidenceToSummary';

type SummaryMatchStoredState = {
  encodedParams: Record<string, string> | null;
  sessionKey: string | null;
  stats: SummaryMatchStats | null;
  tab: string | null;
};

const emptyState: SummaryMatchStoredState = {
  encodedParams: null,
  sessionKey: null,
  stats: null,
  tab: null,
};

export type SummaryMatchSetPayload = {
  encodedParams: Record<string, string>;
  sessionKey: string;
  stats: SummaryMatchStats;
  tab: string;
};

type SummaryMatchContextValue = {
  isActive: boolean;
  encodedParams: Record<string, string> | null;
  sessionKey: string | null;
  stats: SummaryMatchStats | null;
  tab: string | null;
  resultTotalCount: number | null;
  setMatch: (payload: SummaryMatchSetPayload) => void;
  setResultTotalCount: (count: number) => void;
  clearMatch: () => void;
};

const SummaryMatchContext = createContext<SummaryMatchContextValue | null>(null);

const inactiveValue: SummaryMatchContextValue = {
  isActive: false,
  encodedParams: null,
  sessionKey: null,
  stats: null,
  tab: null,
  resultTotalCount: null,
  setMatch: () => {},
  setResultTotalCount: () => {},
  clearMatch: () => {},
};

export function SummaryMatchProvider({ children, scopeKey }: { children: ReactNode; scopeKey: string }) {
  const [state, setState] = useState<SummaryMatchStoredState>(emptyState);
  const [storedScopeKey, setStoredScopeKey] = useState<string | null>(null);
  const [resultTotalCount, setResultTotalCountState] = useState<number | null>(null);

  useEffect(() => {
    if (storedScopeKey != null && storedScopeKey !== scopeKey) {
      setState(emptyState);
      setStoredScopeKey(null);
      setResultTotalCountState(null);
    }
  }, [scopeKey, storedScopeKey]);

  const isActive =
    state.encodedParams != null && Object.keys(state.encodedParams).length > 0 && storedScopeKey === scopeKey;

  const setMatch = useCallback(
    (payload: SummaryMatchSetPayload) => {
      setState({
        encodedParams: payload.encodedParams,
        sessionKey: payload.sessionKey,
        stats: payload.stats,
        tab: payload.tab,
      });
      setStoredScopeKey(scopeKey);
      setResultTotalCountState(null);
    },
    [scopeKey],
  );

  const setResultTotalCount = useCallback((count: number) => {
    if (!Number.isFinite(count) || count < 0) return;
    setResultTotalCountState(Math.trunc(count));
  }, []);

  const clearMatch = useCallback(() => {
    setState(emptyState);
    setStoredScopeKey(null);
    setResultTotalCountState(null);
  }, []);

  const value = useMemo<SummaryMatchContextValue>(
    () => ({
      isActive,
      encodedParams: isActive ? state.encodedParams : null,
      sessionKey: isActive ? state.sessionKey : null,
      stats: isActive ? state.stats : null,
      tab: isActive ? state.tab : null,
      resultTotalCount: isActive ? resultTotalCount : null,
      setMatch,
      setResultTotalCount,
      clearMatch,
    }),
    [
      isActive,
      state.encodedParams,
      state.sessionKey,
      state.stats,
      state.tab,
      resultTotalCount,
      setMatch,
      setResultTotalCount,
      clearMatch,
    ],
  );

  return <SummaryMatchContext.Provider value={value}>{children}</SummaryMatchContext.Provider>;
}

export function useSummaryMatchContext() {
  return useContext(SummaryMatchContext) ?? inactiveValue;
}

export function buildSummaryMatchScopeKey({
  tab,
  view,
  source,
  searchItems,
}: {
  tab: string;
  view: string;
  source: string;
  searchItems: Array<{ id?: string }> | null | undefined;
}) {
  const itemIds = (searchItems ?? []).map(item => String(item?.id ?? '')).join(',');
  return `${String(tab)}|${String(view)}|${String(source)}|${itemIds}`;
}
